/**
 * htmlRenderer.ts — Renderizador HTML do Studio (saída PDF / e-mail)
 *
 * FONTE ÚNICA: este arquivo é o compilador HTML do Studio e é consumido
 * simultaneamente pelas Edge Functions (Deno) e pelo aplicativo React
 * (`src/lib/studio/render/html/index.ts` apenas reexporta este módulo).
 * Não existe cópia espelhada — evitar divergência entre pré-visualização e PDF
 * é justamente o motivo de o arquivo morar em um lugar só.
 *
 * Regras:
 * - Sem dependência de DOM, React, Node ou Deno (apenas `crypto.subtle`, que
 *   existe nos dois ambientes, e ainda assim isolado em função própria).
 * - A saída reproduz fielmente o HTML que a proposta em PDF já gerava, agora
 *   dirigida pelos blocos do documento do Studio.
 * - Blocos de ação (`action.*`) não têm representação em PDF/e-mail e são
 *   ignorados silenciosamente.
 *
 * RELEASE: v1.14.0 - 2026-09-11 - Faixa de borda a borda (`bleed`) e vídeo com capa,
 *            botão de play sem JavaScript e link normal do YouTube/Vimeo/Loom.
 * RELEASE: v1.10.0 - 2026-09-11 - Movimento pelo registro versionado, com orçamento
 *            de interação por página e disparo por rolagem sem JavaScript — Fase 5
 * RELEASE: v1.6.0 - 2026-09-10 - `baseStyles()` passa a ser exportada, para que a
 *                                página pública (prévia e publicação) inclua a
 *                                MESMA folha de estilo usada pelo compilador.
 *                                Nenhuma regra de saída foi alterada.
 * RELEASE: v1.4.0 - 2026-09-10 - Motor de modelos: seção com camadas
 *                                (`section.layered`) e primitivos `ui.*`
 *                                (selo, título, parágrafo, botões, imagem,
 *                                cartão, prova social, número, logos e
 *                                moldura), com estilos de camada e movimento.
 * RELEASE: v1.3.0 - 2026-09-10 - Blocos de seção genéricos do construtor de
 *                                blocos prontos (título, texto com imagem,
 *                                galeria, vídeo, números, logos, preços,
 *                                perguntas, etapas, equipe, chamada, prazo,
 *                                contato, menu, rodapé e faixa) e compilação
 *                                recursiva de `layout.section`/`layout.columns`.
 * RELEASE: v1.9.0 - 2026-09-11 - Efeitos visuais por registro versionado: o bloco
 *                                referencia o efeito pela chave e o compilador
 *                                aplica o substituto quando o meio (PDF/e-mail)
 *                                não suporta.
 * RELEASE: v1.8.0 - 2026-09-11 - Camadas e posicionamento governado: blocos com
 *                                o grupo `layer` saem envolvidos por uma camada
 *                                (posição, profundidade, âncora, deslocamento,
 *                                giro, escala e opacidade por tamanho de tela).
 * RELEASE: v1.7.0 - 2026-09-11 - Colunas passam a usar a grade interna de 12
 *                                colunas com proporções por tamanho de tela.
 * RELEASE: v1.2.0 - 2026-09-10 - Formulário de página envia direto ao servidor
 *                                (`data.formAction` + campos ocultos de rota),
 *                                sem depender de JavaScript na página publicada.
 * RELEASE: v1.1.0 - 2026-09-10 - Blocos de página pública (landing): page.hero,
 *                                page.features, page.testimonials, page.form,
 *                                common.image e common.button; common.text passa
 *                                a aceitar conteúdo em `html` (já sanitizado).
 * RELEASE: v1.0.0 - 2026-09-09 - Versão inicial (Studio, Fase 3 — PDF pela mesma fonte)
 */

// =============================================================================
// Versão do compilador — usada em `content_template_versions.compiler_version`.
// Ao alterar qualquer regra de saída aqui, incrementar. O cache compilado é
// invalidado automaticamente quando a versão gravada difere desta.
// =============================================================================
export const STUDIO_COMPILER_VERSION = 'html-1.15.0';

// =============================================================================
// Métricas de página (espelham src/lib/document/pageMetrics.ts, em valores
// absolutos para manter este módulo sem imports em ambiente Deno)
// =============================================================================
export const STUDIO_A4_WIDTH_MM = 210;
export const STUDIO_A4_HEIGHT_MM = 297;
/** Margem padrão de documento A4 usada na conversão (mm) */
export const STUDIO_A4_MARGIN_MM = 14;

// =============================================================================
// Tipos mínimos do documento (subconjunto neutro do Studio Core)
// =============================================================================

export interface StudioHtmlBlock {
  id: string;
  type: string;
  props?: Record<string, unknown>;
  /** Blocos filhos (usados por `layout.section` e `layout.columns`). */
  children?: StudioHtmlBlock[];
}


export interface StudioHtmlDocument {
  schemaVersion?: number;
  theme?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  blocks: StudioHtmlBlock[];
}

/** Dados resolvidos entregues ao compilador (snapshot comercial da proposta). */
// deno-lint-ignore no-explicit-any
export type StudioHtmlData = Record<string, any>;

export interface StudioHtmlResult {
  html: string;
  compilerVersion: string;
}

// =============================================================================
// Utilitários de formatação (comportamento idêntico ao renderizador anterior)
// =============================================================================

import { resolveAssetUrl } from './stockCatalog.ts';
// Registro seguro de ícones: o documento só informa a chave, o desenho é nosso.
import { studioIconSvg } from './icons.ts';

// Grade interna de 12 colunas — fonte única compartilhada com o editor.
import { classesDaColuna } from './grid.ts';
// Camadas e posicionamento governado (Fase 3) — mesma fonte usada pelo editor
import { classesDaCamada, estiloDaCamada, temCamada } from './layers.ts';
// Efeitos visuais versionados (Fase 4) — mesmo registro usado pelo editor
import { classeDoEfeito, lerEfeito, type StudioEffectRenderMode } from './effects.ts';
// Direção de arte por tela (Fase 7): visibilidade e ordem por tamanho de tela.
import { blocoVisivelNoModo, classeDeVisibilidade, estiloDaOrdem, temRegraDeTela } from './effects.ts';
// Registro visual (Fase 6): recortes, inclinação e formas decorativas.
import {
  classeDaForma,
  classesDoVisual,
  estiloDoVisual,
  type StudioVisualRenderMode,
} from './effects.ts';
// Leitura de endereços de vídeo (YouTube, Vimeo, Loom, arquivo) — fonte única
import { lerVideo } from './videoEmbed.ts';
// Blocos interativos oficiais (biblioteca própria, Fases 3 e 4)
import {
  listStudioInteractiveBlocks,
  renderStudioInteractiveBlock,
  type StudioInteractiveMode,
} from './interactive.ts';
// Seção em HTML livre: montagem única compartilhada com o editor
import {
  renderStudioHtmlSection,
  type StudioHtmlSectionProps,
} from './htmlSections.ts';
// Movimento declarativo com orçamento de interação (Fase 5)
import {
  criarOrcamentoDeMovimento,
  type StudioMotionBudget,
  type StudioMotionRenderMode,
} from './motion.ts';
// Fundos decorativos e acabamentos de borda (biblioteca própria, Fase 1)
import {
  classesDoDecor,
  estiloDoDecor,
  temDecor,
  type StudioDecorRenderMode,
} from './decor.ts';

/**
 * Escapa um endereço de imagem já resolvendo a referência do acervo
 * compartilhado (`acervo://<chave>`). Endereços comuns passam intactos.
 */
function escapeImg(valor: unknown): string {
  return escapeHtml(resolveAssetUrl(valor));
}

/** Escapa caracteres perigosos para evitar injeção de HTML */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Formata valores monetários no padrão brasileiro */
export function formatMoney(value: unknown, currency = 'BRL'): string {
  const n = Number(value ?? 0);
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(n);
  } catch (_) {
    return `R$ ${n.toFixed(2)}`;
  }
}

/** Formata datas ISO em dd/MM/yyyy (fuso São Paulo) */
export function formatDate(value: unknown): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo' }).format(
      new Date(String(value)),
    );
  } catch (_) {
    return '—';
  }
}

/** Minifica o HTML para reduzir o payload enviado à conversão */
export function minifyHtml(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\n\s*/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/>\s+</g, '><')
    .trim();
}

/** SHA-256 em hexadecimal (auditoria de integridade do HTML compilado) */
export async function sha256Hex(input: string | Uint8Array): Promise<string> {
  const bytes = typeof input === 'string' ? new TextEncoder().encode(input) : input;
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// =============================================================================
// Folha de estilo base do documento impresso
// =============================================================================

// As folhas de estilo moram no núcleo neutro do Studio (fonte única compartilhada
// com o editor). Reexportadas aqui para não quebrar quem já importa daqui.
import { baseStyles, layerStyles } from './studioStyles.ts';

export { baseStyles, layerStyles };

// =============================================================================
// Compiladores por bloco (registro de saída HTML)
// =============================================================================

type BlockHtmlCompiler = (
  block: StudioHtmlBlock,
  data: StudioHtmlData,
) => string;

/** Cabeçalho da proposta: marca, metadados, cartões de cliente/negócio, título */
const compileProposalHero: BlockHtmlCompiler = (_block, data) => {
  const p = data.proposal ?? {};
  const tenant = data.tenant ?? {};
  const customer = data.customer ?? null;
  const contact = data.contact ?? null;
  const seller = data.seller ?? null;

  const clientName = customer?.name || contact?.name || '—';
  const clientDoc = customer?.document
    ? `<div class="muted">Documento: ${escapeHtml(customer.document)}</div>`
    : '';
  const clientCity =
    customer?.city || customer?.state
      ? `<div class="muted">${escapeHtml(
          [customer?.city, customer?.state].filter(Boolean).join(' / '),
        )}</div>`
      : '';
  const clientPhone = contact?.phone
    ? `<div class="muted">Contato: ${escapeHtml(contact.phone)}</div>`
    : '';

  return `
    <div class="header">
      <div>
        ${
          tenant.logo_url
            ? `<img class="logo" src="${escapeHtml(tenant.logo_url)}" alt="${escapeHtml(tenant.name)}" />`
            : `<div class="brand">${escapeHtml(tenant.name)}</div>`
        }
        ${tenant.phone ? `<div class="muted">${escapeHtml(tenant.phone)}</div>` : ''}
      </div>
      <div class="doc-meta">
        <div class="doc-title">Proposta Comercial</div>
        <div>Nº ${escapeHtml(p.proposal_number)} · versão ${escapeHtml(p.version_number)}</div>
        <div>Emissão: ${formatDate(p.created_at)}</div>
        <div>Validade: ${p.expires_at ? formatDate(p.expires_at) : 'Sem prazo definido'}</div>
      </div>
    </div>

    <div class="cards">
      <div class="card">
        <h4>Cliente</h4>
        <strong>${escapeHtml(clientName)}</strong>
        ${clientDoc}
        ${clientCity}
        ${clientPhone}
      </div>
      <div class="card">
        <h4>Negócio</h4>
        <strong>${escapeHtml(data.deal?.title)}</strong>
        ${seller?.name ? `<div class="muted">Responsável: ${escapeHtml(seller.name)}</div>` : ''}
      </div>
    </div>

    ${p.title ? `<h3 class="section">${escapeHtml(p.title)}</h3>` : ''}
    ${
      p.description
        ? `<div class="conditions" style="margin-bottom:14px;">${escapeHtml(p.description)}</div>`
        : ''
    }`;
};

/** Tabela de itens (snapshot fiel do momento da geração) */
const compileProposalItems: BlockHtmlCompiler = (_block, data) => {
  const p = data.proposal ?? {};
  const currency = p.currency || 'BRL';
  // deno-lint-ignore no-explicit-any
  const items: any[] = Array.isArray(data.items) ? data.items : [];

  const rows = items
    .map((it) => {
      const recurrence = it.recurring_price
        ? `<div class="muted">Recorrente: ${formatMoney(it.recurring_price, currency)}${
            it.recurrence_interval ? ` / ${escapeHtml(it.recurrence_interval)}` : ''
          }</div>`
        : '';
      const notes = it.notes ? `<div class="muted">${escapeHtml(it.notes)}</div>` : '';
      const discount =
        Number(it.discount_percent ?? 0) > 0
          ? `${Number(it.discount_percent).toFixed(2)}%`
          : Number(it.discount_fixed ?? 0) > 0
          ? formatMoney(it.discount_fixed, currency)
          : '—';

      return `
        <tr>
          <td>
            <strong>${escapeHtml(it.product_name)}</strong>
            ${it.product_sku ? `<div class="muted">SKU: ${escapeHtml(it.product_sku)}</div>` : ''}
            ${recurrence}
            ${notes}
          </td>
          <td class="center">${Number(it.quantity ?? 0)}${it.unit_type ? ` ${escapeHtml(it.unit_type)}` : ''}</td>
          <td class="right">${formatMoney(it.unit_price, currency)}</td>
          <td class="center">${discount}</td>
          <td class="right"><strong>${formatMoney(it.total, currency)}</strong></td>
        </tr>`;
    })
    .join('');

  return `
    <h3 class="section">Itens da proposta</h3>
    <table>
      <thead>
        <tr>
          <th>Produto / Serviço</th>
          <th class="center">Qtd.</th>
          <th class="right">Valor unit.</th>
          <th class="center">Desconto</th>
          <th class="right">Total</th>
        </tr>
      </thead>
      <tbody>
        ${rows || '<tr><td colspan="5">Nenhum item nesta proposta.</td></tr>'}
      </tbody>
    </table>`;
};

/** Totais (subtotal, descontos, MRR e total geral) */
const compileProposalTotals: BlockHtmlCompiler = (_block, data) => {
  const p = data.proposal ?? {};
  const currency = p.currency || 'BRL';
  return `
    <table class="totals">
      <tr><td>Subtotal</td><td class="right">${formatMoney(p.subtotal, currency)}</td></tr>
      <tr><td>Descontos</td><td class="right">- ${formatMoney(p.discount_total, currency)}</td></tr>
      ${
        Number(p.mrr_total ?? 0) > 0
          ? `<tr><td>Recorrência mensal (MRR)</td><td class="right">${formatMoney(p.mrr_total, currency)}</td></tr>`
          : ''
      }
      <tr class="grand"><td class="grand">Total</td><td class="right grand">${formatMoney(p.total, currency)}</td></tr>
    </table>`;
};

/** Condições comerciais (HTML já sanitizado na origem) */
const compileProposalConditions: BlockHtmlCompiler = (_block, data) => {
  const p = data.proposal ?? {};
  if (!p.conditions_html) return '';
  return `<h3 class="section">Condições comerciais</h3><div class="conditions">${p.conditions_html}</div>`;
};

/** Rodapé com aviso de geração eletrônica */
const compileProposalFooter: BlockHtmlCompiler = (_block, data) => {
  const tenant = data.tenant ?? {};
  return `
    <div class="footer-note">
      Documento gerado eletronicamente por ${escapeHtml(tenant.name)} em ${formatDate(
    new Date().toISOString(),
  )}.
    </div>`;
};

/**
 * Texto livre (bloco de conteúdo genérico).
 * Aceita `content` (texto puro, escapado) ou `html` (conteúdo do editor, que já
 * passou pelo saneador autoritativo do servidor ao salvar/publicar).
 */
const compileCommonText: BlockHtmlCompiler = (block) => {
  const html = String(block.props?.html ?? '');
  if (html) {
    const align = String(block.props?.align ?? 'left');
    const color = String(block.props?.color ?? '');
    return `<div class="block-text" style="text-align:${escapeHtml(align)};${
      color ? `color:${escapeHtml(color)};` : ''
    }">${html}</div>`;
  }
  const content = String(block.props?.content ?? '');
  if (!content) return '';
  return `<div class="block-text">${escapeHtml(content)}</div>`;
};

/** Imagem comum */
const compileCommonImage: BlockHtmlCompiler = (block) => {
  const src = String(block.props?.src ?? '');
  if (!src) return '';
  const width = String(block.props?.width ?? '100%');
  return `<img class="page-image" src="${escapeImg(src)}" alt="${escapeHtml(
    block.props?.alt ?? '',
  )}" style="width:${escapeHtml(width)}" />`;
};

/** Botão comum (link) */
const compileCommonButton: BlockHtmlCompiler = (block) => {
  const label = String(block.props?.label ?? 'Clique aqui');
  const href = String(block.props?.href ?? '#');
  const bg = String(block.props?.backgroundColor ?? '#2563eb');
  const fg = String(block.props?.textColor ?? '#ffffff');
  return `<a class="page-button" href="${escapeHtml(href)}" style="background:${escapeHtml(
    bg,
  )};color:${escapeHtml(fg)}">${escapeHtml(label)}</a>`;
};

// =============================================================================
// Blocos de página pública (landing)
// =============================================================================

/** Divide "a|b|c" por linha em partes já limpas. */
function parseLinhasHtml(valor: unknown): string[][] {
  return String(valor ?? '')
    .split('\n')
    .map((linha) => linha.trim())
    .filter(Boolean)
    .map((linha) => linha.split('|').map((parte) => parte.trim()));
}

/** Capa da página */
const compilePageHero: BlockHtmlCompiler = (block) => {
  const align = String(block.props?.align ?? 'center') === 'left' ? 'left' : 'center';
  const bg = String(block.props?.backgroundColor ?? '');
  const fg = String(block.props?.textColor ?? '');
  const imagem = String(block.props?.imageUrl ?? '');
  const ctaLabel = String(block.props?.ctaLabel ?? '');
  const ctaHref = String(block.props?.ctaHref ?? '#formulario');

  return `
    <section class="page-hero" style="text-align:${align};${bg ? `background:${escapeHtml(bg)};` : ''}${
    fg ? `color:${escapeHtml(fg)};` : ''
  }">
      <h1>${escapeHtml(block.props?.title)}</h1>
      ${block.props?.subtitle ? `<p>${escapeHtml(block.props.subtitle)}</p>` : ''}
      ${imagem ? `<img src="${escapeImg(imagem)}" alt="${escapeHtml(block.props?.title)}" />` : ''}
      ${ctaLabel ? `<a class="page-cta" href="${escapeHtml(ctaHref)}">${escapeHtml(ctaLabel)}</a>` : ''}
    </section>`;
};

/** Lista de destaques */
const compilePageFeatures: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items);
  const colunas = Number(block.props?.columns) || 3;
  const cards = itens
    .map(
      ([titulo, descricao]) => `
        <div class="page-card">
          <h3>${escapeHtml(titulo)}</h3>
          ${descricao ? `<p>${escapeHtml(descricao)}</p>` : ''}
        </div>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="grid-template-columns:repeat(${colunas},minmax(0,1fr))">${cards}</div>
    </section>`;
};

/** Depoimentos */
const compilePageTestimonials: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items);
  const cards = itens
    .map(
      ([texto, nome, empresa]) => `
        <figure class="page-card" style="margin:0">
          <blockquote class="page-quote">&ldquo;${escapeHtml(texto)}&rdquo;</blockquote>
          <figcaption class="page-quote-author">${escapeHtml(nome)}${
        empresa ? ` &middot; ${escapeHtml(empresa)}` : ''
      }</figcaption>
        </figure>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="grid-template-columns:repeat(2,minmax(0,1fr))">${cards}</div>
    </section>`;
};

/** Campo do formulário na saída HTML */
// deno-lint-ignore no-explicit-any
function compileFormField(campo: any): string {
  const key = escapeHtml(campo?.key);
  const label = escapeHtml(campo?.label);
  const required = campo?.required ? ' required' : '';
  const maxLength = campo?.maxLength ? ` maxlength="${Number(campo.maxLength)}"` : '';
  const placeholder = campo?.placeholder ? ` placeholder="${escapeHtml(campo.placeholder)}"` : '';

  if (campo?.type === 'hidden') {
    return `<input type="hidden" name="${key}" value="${escapeHtml(campo?.defaultValue ?? '')}" />`;
  }
  if (campo?.type === 'consent') {
    return `
      <div class="field consent">
        <input type="checkbox" id="${key}" name="${key}" value="true"${required} />
        <label for="${key}" style="font-weight:400;margin:0">${escapeHtml(
      campo?.consentText || campo?.label,
    )}</label>
      </div>`;
  }
  if (campo?.type === 'textarea') {
    return `
      <div class="field">
        <label for="${key}">${label}</label>
        <textarea id="${key}" name="${key}" rows="4"${required}${maxLength}${placeholder}></textarea>
      </div>`;
  }
  if (campo?.type === 'select') {
    // deno-lint-ignore no-explicit-any
    const opcoes = (Array.isArray(campo?.options) ? campo.options : []) as any[];
    return `
      <div class="field">
        <label for="${key}">${label}</label>
        <select id="${key}" name="${key}"${required}>
          <option value="">Selecione</option>
          ${opcoes
            .map((o) => `<option value="${escapeHtml(o?.value)}">${escapeHtml(o?.label)}</option>`)
            .join('')}
        </select>
      </div>`;
  }

  const tipo =
    campo?.type === 'email' ? 'email' : campo?.type === 'number' ? 'number' : 'text';
  const inputMode = campo?.type === 'phone' ? ' inputmode="tel"' : '';
  return `
    <div class="field">
      <label for="${key}">${label}</label>
      <input type="${tipo}" id="${key}" name="${key}"${inputMode}${required}${maxLength}${placeholder} />
    </div>`;
}

/**
 * Formulário de captura.
 * A definição dos campos vem de `settings.forms[formId]`; o HTML publicado
 * carrega os identificadores em `data-*` para o envio ser resolvido no servidor.
 */
const compilePageForm: BlockHtmlCompiler = (block, data) => {
  const formId = String(block.props?.formId ?? 'lead_capture');
  const definicao = data?.forms?.[formId];
  // deno-lint-ignore no-explicit-any
  const campos: any[] = Array.isArray(definicao?.fields) ? definicao.fields : [];

  return `
    <section class="page-section" id="formulario">
      <div class="page-form">
        ${block.props?.title ? `<h2 style="text-align:left">${escapeHtml(block.props.title)}</h2>` : ''}
        ${
          block.props?.description
            ? `<p style="font-size:14px;color:#6b7280;margin-top:-12px">${escapeHtml(
                block.props.description,
              )}</p>`
            : ''
        }
        <form data-uaizy-form="${escapeHtml(formId)}"
              method="post"
              action="${escapeHtml(data?.formAction ?? '')}">
          <input type="hidden" name="form_id" value="${escapeHtml(formId)}" />
          <input type="hidden" name="page_path" value="${escapeHtml(data?.pagePath ?? '')}" />
          <input type="hidden" name="page_host" value="${escapeHtml(data?.pageHost ?? '')}" />
          ${campos.map(compileFormField).join('')}
          <input class="page-honeypot" type="text" name="website_company_url" tabindex="-1" autocomplete="off" aria-hidden="true" />
          <button type="submit">${escapeHtml(block.props?.submitLabel ?? 'Enviar')}</button>
        </form>
      </div>
    </section>`;
};

/** Separador visual */
const compileCommonDivider: BlockHtmlCompiler = () => '<hr class="block-divider" />';

/** Espaçador */
const compileLayoutSpacer: BlockHtmlCompiler = (block) => {
  const size = Number(block.props?.size ?? 16);
  return `<div style="height:${Number.isFinite(size) ? size : 16}px"></div>`;
};

// =============================================================================
// Blocos de seção genéricos (construtor de blocos prontos)
// =============================================================================

/** Estilo de grade com o número de colunas pedido. */
function gradeHtml(colunas: unknown, minimo = 3): string {
  const quantidade = Number(colunas) || minimo;
  return `grid-template-columns:repeat(${quantidade},minmax(0,1fr))`;
}

/** Estilo inline de cor de fundo e cor de texto. */
function coresHtml(block: StudioHtmlBlock, chaveFundo = 'backgroundColor', chaveTexto = 'textColor'): string {
  const fundo = String(block.props?.[chaveFundo] ?? '');
  const texto = String(block.props?.[chaveTexto] ?? '');
  return `${fundo ? `background:${escapeHtml(fundo)};` : ''}${texto ? `color:${escapeHtml(texto)};` : ''}`;
}

/** Título de seção */
const compilePageHeading: BlockHtmlCompiler = (block) => {
  const align = String(block.props?.align ?? 'center') === 'left' ? 'left' : 'center';
  const cor = String(block.props?.accentColor ?? '');
  return `
    <section class="page-section" style="text-align:${align}">
      ${block.props?.eyebrow ? `<p class="page-eyebrow"${cor ? ` style="color:${escapeHtml(cor)}"` : ''}>${escapeHtml(block.props.eyebrow)}</p>` : ''}
      ${block.props?.title ? `<h2 style="text-align:${align};margin-bottom:8px">${escapeHtml(block.props.title)}</h2>` : ''}
      ${block.props?.subtitle ? `<p class="page-lead">${escapeHtml(block.props.subtitle)}</p>` : ''}
    </section>`;
};

/** Texto com imagem */
const compilePageSplit: BlockHtmlCompiler = (block) => {
  const imagemPrimeiro = String(block.props?.imagePosition ?? 'right') === 'left';
  const url = String(block.props?.imageUrl ?? '');
  const imagem = url
    ? `<div><img class="page-split-img" src="${escapeImg(url)}" alt="${escapeHtml(block.props?.title)}" /></div>`
    : '<div></div>';
  const texto = `
      <div>
        ${block.props?.eyebrow ? `<p class="page-eyebrow">${escapeHtml(block.props.eyebrow)}</p>` : ''}
        ${block.props?.title ? `<h2 style="text-align:left">${escapeHtml(block.props.title)}</h2>` : ''}
        <div class="block-text">${String(block.props?.text ?? '')}</div>
        ${block.props?.ctaLabel ? `<a class="page-cta" href="${escapeHtml(block.props?.ctaHref ?? '#formulario')}">${escapeHtml(block.props.ctaLabel)}</a>` : ''}
      </div>`;
  return `
    <section class="page-section page-split" style="${coresHtml(block)}">
      ${imagemPrimeiro ? imagem + texto : texto + imagem}
    </section>`;
};

/** Galeria de imagens */
const compilePageGallery: BlockHtmlCompiler = (block) => {
  const proporcao =
    String(block.props?.ratio ?? 'landscape') === 'square'
      ? '1 / 1'
      : String(block.props?.ratio ?? '') === 'portrait'
        ? '3 / 4'
        : '4 / 3';
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([url, legenda]) => `
        <figure style="margin:0">
          <img src="${escapeImg(url)}" alt="${escapeHtml(legenda ?? '')}" style="width:100%;border-radius:12px;object-fit:cover;aspect-ratio:${proporcao}" />
          ${legenda ? `<figcaption class="muted">${escapeHtml(legenda)}</figcaption>` : ''}
        </figure>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns)}">${itens}</div>
    </section>`;
};

/** Vídeo (na saída impressa mostra apenas a capa) */
const compilePageVideo: BlockHtmlCompiler = (block) => {
  const capa = String(block.props?.posterUrl ?? '');
  const video = String(block.props?.videoUrl ?? '');
  const conteudo = video
    ? `<iframe src="${escapeHtml(video)}" title="${escapeHtml(block.props?.title ?? 'Vídeo')}" style="width:100%;aspect-ratio:16/9;border:0;border-radius:16px" allowfullscreen></iframe>`
    : capa
      ? `<img src="${escapeImg(capa)}" alt="${escapeHtml(block.props?.caption ?? '')}" style="width:100%;border-radius:16px" />`
      : '';
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      ${conteudo}
      ${block.props?.caption ? `<p class="muted" style="text-align:center">${escapeHtml(block.props.caption)}</p>` : ''}
    </section>`;
};

/** Números e resultados */
const compilePageStats: BlockHtmlCompiler = (block) => {
  const fundo = String(block.props?.backgroundColor ?? '');
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([valor, descricao]) => `
        <div style="text-align:center">
          <p class="page-stat-value">${escapeHtml(valor)}</p>
          ${descricao ? `<p class="page-stat-label">${escapeHtml(descricao)}</p>` : ''}
        </div>`,
    )
    .join('');
  return `
    <section class="page-section" style="${fundo ? `background:${escapeHtml(fundo)};color:#ffffff;border-radius:16px;` : ''}">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns)}">${itens}</div>
    </section>`;
};

/** Logos de parceiros */
const compilePageLogos: BlockHtmlCompiler = (block) => {
  const cinza = block.props?.grayscale ? 'filter:grayscale(1);opacity:.7;' : '';
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([url, nome]) =>
        `<img src="${escapeImg(url)}" alt="${escapeHtml(nome ?? '')}" style="height:32px;width:auto;object-fit:contain;${cinza}" />`,
    )
    .join('');
  if (!itens) return '';
  return `
    <section class="page-section">
      ${block.props?.title ? `<p class="page-eyebrow" style="text-align:center">${escapeHtml(block.props.title)}</p>` : ''}
      <div class="page-logos">${itens}</div>
    </section>`;
};

/** Planos e preços */
const compilePagePricing: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items)
    .map(([nome, preco, periodo, recursos, destaque, botao, href]) => {
      const emDestaque = String(destaque ?? '').toLowerCase() === 'sim';
      const lista = String(recursos ?? '')
        .split(';')
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => `<li>${escapeHtml(r)}</li>`)
        .join('');
      return `
        <div class="page-card page-plan${emDestaque ? ' page-plan-featured' : ''}">
          <p class="page-eyebrow">${escapeHtml(nome)}</p>
          <p class="page-price">${escapeHtml(preco ?? '')}</p>
          ${periodo ? `<p class="muted">${escapeHtml(periodo)}</p>` : ''}
          <ul class="page-plan-list">${lista}</ul>
          ${botao ? `<a class="page-cta" style="margin-top:16px" href="${escapeHtml(href || '#formulario')}">${escapeHtml(botao)}</a>` : ''}
        </div>`;
    })
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns)}">${itens}</div>
      ${block.props?.note ? `<p class="muted" style="text-align:center;margin-top:14px">${escapeHtml(block.props.note)}</p>` : ''}
    </section>`;
};

/** Perguntas frequentes */
const compilePageFaq: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([pergunta, resposta]) => `
        <div class="page-card">
          <h3>${escapeHtml(pergunta)}</h3>
          ${resposta ? `<p>${escapeHtml(resposta)}</p>` : ''}
        </div>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns, 1)}">${itens}</div>
    </section>`;
};

/** Etapas */
const compilePageSteps: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items);
  const vertical = String(block.props?.layout ?? 'horizontal') === 'vertical';
  const cards = itens
    .map(
      ([titulo, descricao], indice) => `
        <div class="page-card page-step">
          <span class="page-step-number">${indice + 1}</span>
          <div>
            <h3>${escapeHtml(titulo)}</h3>
            ${descricao ? `<p>${escapeHtml(descricao)}</p>` : ''}
          </div>
        </div>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${vertical ? 'grid-template-columns:minmax(0,1fr)' : gradeHtml(String(itens.length || 3))}">${cards}</div>
    </section>`;
};

/** Equipe */
const compilePageTeam: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([foto, nome, cargo]) => `
        <div style="text-align:center">
          ${foto ? `<img src="${escapeImg(foto)}" alt="${escapeHtml(nome ?? '')}" class="page-avatar" />` : '<div class="page-avatar page-avatar-empty"></div>'}
          <p style="font-weight:600;margin:10px 0 0 0">${escapeHtml(nome ?? '')}</p>
          ${cargo ? `<p class="muted">${escapeHtml(cargo)}</p>` : ''}
        </div>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns, 4)}">${itens}</div>
    </section>`;
};

/** Chamada para ação */
const compilePageCta: BlockHtmlCompiler = (block) => {
  const align = String(block.props?.align ?? 'center') === 'left' ? 'left' : 'center';
  return `
    <section class="page-hero" style="text-align:${align};padding:40px 24px;${coresHtml(block)}">
      ${block.props?.title ? `<h2 style="text-align:${align};margin:0 0 8px 0">${escapeHtml(block.props.title)}</h2>` : ''}
      ${block.props?.subtitle ? `<p>${escapeHtml(block.props.subtitle)}</p>` : ''}
      ${block.props?.buttonLabel ? `<a class="page-cta" href="${escapeHtml(block.props?.buttonHref ?? '#formulario')}">${escapeHtml(block.props.buttonLabel)}</a>` : ''}
    </section>`;
};

/** Prazo / validade */
const compilePageDeadline: BlockHtmlCompiler = (block) => `
    <section class="page-deadline" style="${coresHtml(block)}">
      <p style="font-weight:600;margin:0">${escapeHtml(block.props?.title)}</p>
      ${block.props?.note ? `<p class="muted" style="margin:4px 0 0 0">${escapeHtml(block.props.note)}</p>` : ''}
    </section>`;

/** Contato e endereço */
const compilePageContact: BlockHtmlCompiler = (block) => {
  const itens = parseLinhasHtml(block.props?.items)
    .map(
      ([rotulo, valor]) => `
        <div class="page-card" style="text-align:center">
          <p class="page-eyebrow">${escapeHtml(rotulo)}</p>
          <p style="margin:4px 0 0 0;font-weight:600">${escapeHtml(valor ?? '')}</p>
        </div>`,
    )
    .join('');
  return `
    <section class="page-section">
      ${block.props?.title ? `<h2>${escapeHtml(block.props.title)}</h2>` : ''}
      <div class="page-grid st-grid-auto" style="${gradeHtml(block.props?.columns)}">${itens}</div>
    </section>`;
};

/** Menu do topo */
const compilePageNav: BlockHtmlCompiler = (block) => {
  const fundo = String(block.props?.backgroundColor ?? '');
  const links = parseLinhasHtml(block.props?.links)
    .map(([rotulo, destino]) => `<a href="${escapeHtml(destino || '#')}">${escapeHtml(rotulo)}</a>`)
    .join('');
  return `
    <nav class="page-nav" style="${fundo ? `background:${escapeHtml(fundo)};color:#ffffff;` : ''}">
      <div class="page-nav-brand">
        ${block.props?.logoUrl ? `<img src="${escapeHtml(block.props.logoUrl)}" alt="" style="height:28px;width:auto" />` : ''}
        <span>${escapeHtml(block.props?.brand ?? '')}</span>
      </div>
      <div class="page-nav-links">
        ${links}
        ${block.props?.ctaLabel ? `<a class="page-nav-cta" href="${escapeHtml(block.props?.ctaHref ?? '#formulario')}">${escapeHtml(block.props.ctaLabel)}</a>` : ''}
      </div>
    </nav>`;
};

/** Rodapé */
const compilePageFooter: BlockHtmlCompiler = (block) => {
  const links = parseLinhasHtml(block.props?.links)
    .map(([rotulo, destino]) => `<a href="${escapeHtml(destino || '#')}">${escapeHtml(rotulo)}</a>`)
    .join('');
  return `
    <footer class="page-footer" style="${coresHtml(block)}">
      <p style="font-weight:600;margin:0">${escapeHtml(block.props?.brand ?? '')}</p>
      ${block.props?.text ? `<p style="margin:4px 0 0 0;opacity:.8">${escapeHtml(block.props.text)}</p>` : ''}
      ${links ? `<div class="page-footer-links">${links}</div>` : ''}
      ${block.props?.legal ? `<p class="muted" style="margin-top:18px">${escapeHtml(block.props.legal)}</p>` : ''}
    </footer>`;
};

/** Faixa de aviso */
const compilePageBanner: BlockHtmlCompiler = (block) => `
    <div class="page-banner" style="${coresHtml(block)}">
      <span>${escapeHtml(block.props?.text)}</span>
      ${block.props?.linkLabel ? `<a href="${escapeHtml(block.props?.linkHref ?? '#')}">${escapeHtml(block.props.linkLabel)}</a>` : ''}
    </div>`;

/** Seção com filhos (recursiva) */
const compileLayoutSection: BlockHtmlCompiler = (block, data) => {
  const conteudo = compileChildren(block, data);
  const largura = String(block.props?.maxWidth ?? '');
  return `
    <section style="${block.props?.backgroundColor ? `background:${escapeHtml(String(block.props.backgroundColor))};` : ''}padding:${escapeHtml(String(block.props?.padding ?? '24px'))};${largura ? `max-width:${escapeHtml(largura)};margin:0 auto;` : ''}border-radius:12px">
      ${conteudo}
    </section>`;
};

/** Colunas com filhos (recursiva) */
const compileLayoutColumns: BlockHtmlCompiler = (block, data) => {
  const filhos = Array.isArray(block.children) ? block.children : [];
  // Grade interna de 12 colunas (Fase 2): a proporção escolhida vira classes de
  // largura por tamanho de tela, exatamente como no editor.
  const preset = block.props?.preset as string | { desktop?: string; tablet?: string; mobile?: string } | undefined;
  const colunas = filhos
    .map((filho, indice) => {
      const classes = classesDaColuna(preset, indice, filhos.length, block.props?.count);
      return `<div class="${escapeHtml(classes)}">${compileBlockHtml(filho, data)}</div>`;
    })
    .join('');
  const alinhamento = String(block.props?.alignItems ?? '');
  return `<div class="st-grid" style="gap:${escapeHtml(String(block.props?.gap ?? '16px'))}${alinhamento ? `;align-items:${escapeHtml(alinhamento)}` : ''}">${colunas}</div>`;
};

// =============================================================================
// Primitivos e seção com camadas (motor de modelos)
// =============================================================================

/**
 * Orçamento de interação da renderização em curso (Fase 5).
 * É trocado a cada `renderStudioBody`, para que cada página tenha o seu teto.
 */
let ORCAMENTO_MOVIMENTO: StudioMotionBudget = criarOrcamentoDeMovimento('published');

/**
 * Classe de movimento do bloco (vazia quando "nenhum", quando o meio não anima
 * — impressão e e-mail — ou quando a página já estourou o teto de animações).
 */
function movimentoHtml(props: unknown): string {
  const classes = ORCAMENTO_MOVIMENTO.classes(props);
  return classes ? ` ${escapeHtml(classes)}` : '';
}

/** Valor de propriedade como texto simples. */
function propTexto(valor: unknown): string {
  return valor == null ? '' : String(valor);
}

/** Seção com camadas (fundo, sobreposição, textura, brilho) — recursiva */
const compileLayeredSection: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const gradiente = propTexto(p.backgroundGradient);
  const imagem = propTexto(p.backgroundImage);
  const overlay = propTexto(p.overlay);
  const padrao = propTexto(p.pattern);
  const brilho = propTexto(p.glow);
  const altura = propTexto(p.minHeight) || 'auto';
  const disposicao = propTexto(p.layout) || 'stack';

  const camadas = [
    gradiente && gradiente !== 'none' ? `<div class="st-bg st-gr-${escapeHtml(gradiente)}"></div>` : '',
    imagem ? `<div class="st-bg" style="background-image:url('${escapeImg(imagem)}')"></div>` : '',
    overlay && overlay !== 'none' ? `<div class="st-ov st-ov-${escapeHtml(overlay)}"></div>` : '',
    padrao && padrao !== 'none' ? `<div class="st-pat st-pat-${escapeHtml(padrao)}"></div>` : '',
    brilho && brilho !== 'none' ? `<div class="st-glow st-glow-${escapeHtml(brilho)}"></div>` : '',
  ].join('');

  const estilo = [
    p.backgroundColor ? `background-color:${escapeHtml(propTexto(p.backgroundColor))};` : '',
    p.textColor ? `color:${escapeHtml(propTexto(p.textColor))};` : '',
  ].join('');

  return `
    <section class="st-sec${propTexto(p.bleed) === 'contained' ? ' st-contained' : ''} st-r-${escapeHtml(
      propTexto(p.radius) || 'none',
    )}${altura !== 'auto' ? ` st-h-${escapeHtml(altura)}` : ''}${movimentoHtml(p)}" style="${estilo}">
      ${camadas}
      <div class="st-inner st-pad-${escapeHtml(propTexto(p.paddingY) || 'lg')}${
        disposicao !== 'stack' ? ` st-lay-${escapeHtml(disposicao)}` : ''
      }${p.allowLayers === true ? ' st-layers' : ''} st-al-${escapeHtml(propTexto(p.align) || 'left')}" style="max-width:${escapeHtml(
        propTexto(p.maxWidth) || '1120px',
      )}">
        ${compileChildren(block, data)}
      </div>
    </section>`;
};

/** Contêiner vertical: agrupa vários blocos dentro de uma coluna. */
const compileLayoutStack: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const borda = propTexto(p.border);
  const classes = [
    'st-stack',
    `st-stack-gap-${propTexto(p.gap) || 'md'}`,
    `st-stack-al-${propTexto(p.align) || 'start'}`,
    `st-stack-jt-${propTexto(p.justify) || 'start'}`,
    `st-stack-pad-${propTexto(p.padding) || 'none'}`,
    `st-r-${propTexto(p.radius) || 'none'}`,
    borda && borda !== 'none' ? `st-stack-bd-${borda}` : '',
    p.sticky === true ? 'st-stack-sticky' : '',
  ]
    .filter(Boolean)
    .map((classe) => escapeHtml(classe))
    .join(' ');
  const estilo = [
    p.maxWidth ? `max-width:${escapeHtml(propTexto(p.maxWidth))};` : '',
    p.backgroundColor ? `background:${escapeHtml(propTexto(p.backgroundColor))};` : '',
    p.textColor ? `color:${escapeHtml(propTexto(p.textColor))};` : '',
  ].join('');
  return `<div class="${classes}${movimentoHtml(p)}" style="${estilo}">${compileChildren(block, data)}</div>`;
};

/** Selo */
const compileUiBadge: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const variante = propTexto(p.variant) || 'soft';
  const cor = propTexto(p.accentColor);
  const estilo = cor ? (variante === 'solid' ? `background:${escapeHtml(cor)}` : `color:${escapeHtml(cor)}`) : '';
  return `
    <div class="st-al-${escapeHtml(propTexto(p.align) || 'left')}">
      <span class="st-badge st-badge-${escapeHtml(variante)}${movimentoHtml(p)}" style="${estilo}">
        ${variante === 'line' ? '<span class="st-badge-rule"></span>' : ''}${
          p.prefix ? `<span>${escapeHtml(p.prefix)}</span>` : ''
        }${escapeHtml(p.text)}
      </span>
    </div>`;
};

/** Título */
const compileUiTitle: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const tag = ['h1', 'h2', 'h3'].includes(propTexto(p.level)) ? propTexto(p.level) : 'h1';
  const conteudo = propTexto(p.text);
  const destaque = propTexto(p.highlight);
  let corpo = escapeHtml(conteudo);
  if (destaque && conteudo.includes(destaque)) {
    const partes = conteudo.split(destaque).map((parte) => escapeHtml(parte));
    corpo = partes.join(
      `<span style="color:${escapeHtml(propTexto(p.accentColor) || 'inherit')}">${escapeHtml(destaque)}</span>`,
    );
  }
  const caixa = propTexto(p.transform) || 'inherit';
  const peso = propTexto(p.weight) || 'inherit';
  const pesosValidos = ['400', '500', '600', '700', '800', '900'];
  const classes = [
    'st-title',
    `st-title-${propTexto(p.size) || 'xl'}`,
    `st-al-${propTexto(p.align) || 'left'}`,
    caixa === 'uppercase' || caixa === 'none' ? `st-tt-${caixa}` : '',
    `st-lead-${propTexto(p.leading) || 'normal'}`,
  ]
    .filter(Boolean)
    .map((classe) => escapeHtml(classe))
    .join(' ');
  const estilo = [
    p.color ? `color:${escapeHtml(propTexto(p.color))};` : '',
    pesosValidos.includes(peso) ? `font-weight:${peso};` : '',
    p.maxWidth ? `max-width:${escapeHtml(propTexto(p.maxWidth))};` : '',
    p.preserveLineBreaks === true ? 'white-space:pre-line;' : '',
  ].join('');
  return `<${tag} class="${classes}${movimentoHtml(p)}" style="${estilo}">${corpo}</${tag}>`;
};

/** Parágrafo (conteúdo já sanitizado ao salvar) */
const compileUiParagraph: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const alinhamento = propTexto(p.align) || 'left';
  const estilo = [
    p.color ? `color:${escapeHtml(propTexto(p.color))};` : '',
    p.maxWidth ? `max-width:${escapeHtml(propTexto(p.maxWidth))};` : '',
    alinhamento === 'center' ? 'margin-left:auto;margin-right:auto;' : '',
  ].join('');
  return `<div class="st-p st-p-${escapeHtml(propTexto(p.size) || 'md')} st-al-${escapeHtml(
    alinhamento,
  )}${movimentoHtml(p)}" style="${estilo}">${propTexto(p.text)}</div>`;
};

/** Botões */
const compileUiButtons: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const alinhamento = propTexto(p.align) || 'left';
  const tamanho = propTexto(p.size) || 'md';
  const cor = propTexto(p.accentColor);
  const forma = propTexto(p.shape) || 'rounded';
  const seta = p.arrow === true ? '<span class="st-btn-arrow">→</span>' : '';
  const botoes = parseLinhasHtml(p.items)
    .map(([rotulo, destino, estilo]) => {
      const variante = estilo || 'solid';
      const fundo = cor && variante === 'solid' ? ` style="background:${escapeHtml(cor)}"` : '';
      const classes = `st-btn st-btn-${escapeHtml(tamanho)} st-btn-${escapeHtml(variante)} st-btn-shape-${escapeHtml(
        forma,
      )}${p.uppercase === true ? ' st-btn-caps' : ''}`;
      return `<a class="${classes}" href="${escapeHtml(destino || '#')}"${fundo}>${escapeHtml(rotulo)}${seta}</a>`;
    })
    .join('');
  return `<div class="st-btns${alinhamento === 'center' ? ' st-btns-center' : ''}${
    alinhamento === 'right' ? ' st-btns-right' : ''
  }${movimentoHtml(p)}">${botoes}</div>`;
};


/** Imagem */
const compileUiImage: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const proporcao = propTexto(p.ratio) || 'auto';
  const vis = visualHtml(p, data);
  return `<img class="st-img${proporcao !== 'auto' ? ` st-ratio-${escapeHtml(proporcao)}` : ''} st-r-${escapeHtml(
    propTexto(p.radius) || 'lg',
  )} st-sh-${escapeHtml(propTexto(p.shadow) || 'md')}${vis.classe}${movimentoHtml(p)}" src="${escapeImg(
    p.src,
  )}" alt="${escapeHtml(p.alt)}" style="width:${escapeHtml(propTexto(p.width) || '100%')};${vis.estilo}" />`;
};

/**
 * Acabamento visual do bloco (Fase 6): classe do recorte/inclinação e estilo em
 * linha do traçado próprio, já resolvidos para o modo de saída.
 */
function visualHtml(p: unknown, data: StudioHtmlData): { classe: string; estilo: string } {
  const modo = (data?.__renderMode as StudioVisualRenderMode) ?? 'published';
  const classe = classesDoVisual(p, modo);
  return { classe: classe ? ` ${classe}` : '', estilo: estiloDoVisual(p, modo) };
}

/** Cartão */
const compileUiCard: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const vis = visualHtml(p, data);
  const estilo = [
    vis.estilo,
    p.backgroundColor ? `background:${escapeHtml(propTexto(p.backgroundColor))};` : '',
    p.textColor ? `color:${escapeHtml(propTexto(p.textColor))};` : '',
  ].join('');
  const altura = propTexto(p.minHeight) || 'auto';
  // O ícone vem do registro do sistema; chave desconhecida simplesmente não desenha.
  const icone = studioIconSvg(p.iconName);
  const classes = [
    'st-card',
    `st-card-${propTexto(p.variant) || 'solid'}`,
    `st-r-${propTexto(p.radius) || 'lg'}`,
    `st-sh-${propTexto(p.shadow) || 'md'}`,
    p.highlighted === true ? 'st-card-hl' : '',
    p.hover === true ? 'st-card-hover' : '',
    altura !== 'auto' ? `st-card-h-${altura}` : '',
  ]
    .filter(Boolean)
    .map((classe) => escapeHtml(classe))
    .join(' ');
  return `
    <div class="${classes}${vis.classe}${movimentoHtml(p)}" style="${estilo}">
      ${p.index ? `<span class="st-card-index">${escapeHtml(p.index)}</span>` : ''}
      ${
        icone
          ? `<div class="st-card-badge">${icone}</div>`
          : p.icon
            ? `<div class="st-card-icon">${escapeHtml(p.icon)}</div>`
            : ''
      }
      ${p.eyebrow ? `<p class="st-card-eyebrow">${escapeHtml(p.eyebrow)}</p>` : ''}
      ${p.title ? `<p class="st-card-title">${escapeHtml(p.title)}</p>` : ''}
      ${p.text ? `<p class="st-card-text">${escapeHtml(p.text)}</p>` : ''}
    </div>`;
};

/** Iniciais de um nome, usadas quando a pessoa não tem foto. */
function iniciaisHtml(nome: string): string {
  const partes = String(nome ?? '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '•';
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : '')).toUpperCase();
}

/** Prova social com fotos */
const compileUiAvatars: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const alinhamento = propTexto(p.align) || 'left';
  const itens = parseLinhasHtml(p.items);
  const limite = Math.max(1, Number(propTexto(p.max)) || 5);
  const visiveis = itens.slice(0, limite);
  const excedente = itens.length - visiveis.length;
  const nota = propTexto(p.rating);
  const fotos = visiveis
    .map(([url, nome]) =>
      url
        ? `<img src="${escapeImg(url)}" alt="${escapeHtml(nome ?? '')}" />`
        : `<span class="st-avatar-ini">${escapeHtml(iniciaisHtml(nome ?? ''))}</span>`,
    )
    .join('');
  const mais = excedente > 0 ? `<span class="st-avatar-ini st-avatar-more">+${excedente}</span>` : '';
  return `
    <div class="st-avatars${alinhamento === 'center' ? ' st-avatars-center' : ''}${
      alinhamento === 'right' ? ' st-avatars-right' : ''
    }${movimentoHtml(p)}">
      ${fotos || mais ? `<div class="st-avatars-row">${fotos}${mais}</div>` : ''}
      <div>
        ${nota ? `<div class="st-avatars-rating"><span>★★★★★</span><strong>${escapeHtml(nota)}</strong></div>` : ''}
        ${p.text ? `<div class="st-avatars-text">${escapeHtml(p.text)}</div>` : ''}
        ${p.note ? `<div class="st-avatars-note">${escapeHtml(p.note)}</div>` : ''}
      </div>
    </div>`;
};


/** Número em destaque */
const compileUiStat: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  return `
    <div class="st-al-${escapeHtml(propTexto(p.align) || 'left')}${movimentoHtml(p)}">
      <p class="st-stat-value"${p.accentColor ? ` style="color:${escapeHtml(propTexto(p.accentColor))}"` : ''}>${escapeHtml(
        p.value,
      )}</p>
      <p class="st-stat-label">${escapeHtml(p.label)}</p>
    </div>`;
};

/** Faixa de logos */
const compileUiLogos: BlockHtmlCompiler = (block) => {
  const p = block.props ?? {};
  const logos = parseLinhasHtml(p.items)
    .map(([url, nome]) => `<img src="${escapeImg(url)}" alt="${escapeHtml(nome ?? '')}" />`)
    .join('');
  return `
    <div class="st-logos${p.grayscale === false ? '' : ' st-logos-gray'}${
      propTexto(p.align) === 'center' ? ' st-logos-center' : ''
    }${movimentoHtml(p)}">
      ${p.note ? `<p class="st-logos-note">${escapeHtml(p.note)}</p>` : ''}
      ${logos}
    </div>`;
};

/** Tela em moldura */
const compileUiDevice: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const aparelho = propTexto(p.device) || 'browser';
  const vis = visualHtml(p, data);
  return `
    <div class="st-dev st-dev-${escapeHtml(aparelho)} st-sh-${escapeHtml(
      propTexto(p.shadow) || 'lg',
    )}${vis.classe}${movimentoHtml(p)}"${vis.estilo ? ` style="${vis.estilo}"` : ''}>
      ${aparelho === 'browser' ? '<div class="st-dev-bar"><span></span><span></span><span></span></div>' : ''}
      <img class="st-img" src="${escapeImg(p.src)}" alt="${escapeHtml(p.alt)}" />
      ${aparelho === 'laptop' ? '<div class="st-dev-base"></div>' : ''}
    </div>`;
};

/**
 * Vídeo primitivo. Quando houver imagem de capa, ela é usada (funciona também
 * em PDF e e-mail, onde iframe não é executado); sem capa, cai no iframe.
 */
const compileUiVideo: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const fonte = lerVideo(p.videoUrl);
  const capa = resolveAssetUrl(propTexto(p.posterUrl)) || fonte.posterUrl;
  const raio = escapeHtml(propTexto(p.radius) || 'lg');
  const sombra = escapeHtml(propTexto(p.shadow) || 'md');
  const formato = escapeHtml(propTexto(p.aspect) || '16x9');
  const rotulo = p.playLabel == null ? 'Assistir' : propTexto(p.playLabel);
  const vis = visualHtml(p, data);
  const modo = (data?.__renderMode as StudioEffectRenderMode) ?? 'published';

  // Impressão, PDF e e-mail: nunca um quadro vazio. Sai a capa e, quando há,
  // o endereço do vídeo em texto para quem estiver lendo no papel.
  if (modo === 'pdf' || modo === 'email') {
    if (!capa && !fonte.originalUrl) return '';
    return `
      <div class="st-al-center">
        ${capa ? `<img class="st-img st-r-${raio} st-sh-${sombra}" src="${escapeImg(capa)}" alt="${escapeHtml(p.alt)}" />` : ''}
        ${fonte.originalUrl ? `<p class="st-p st-p-sm st-al-center">Assista em: ${escapeHtml(fonte.originalUrl)}</p>` : ''}
      </div>`;
  }

  const classe = `st-vid st-vid-${formato} st-r-${raio} st-sh-${sombra}${vis.classe}${movimentoHtml(p)}`;
  const estilo = vis.estilo ? ` style="${vis.estilo}"` : '';

  if (!fonte.embedUrl && !fonte.fileUrl) {
    return `
      <div class="${classe}"${estilo}>
        ${capa ? `<img class="st-vid-poster" src="${escapeImg(capa)}" alt="" loading="lazy" />` : ''}
        <div class="st-vid-shade"></div>
      </div>`;
  }

  if (fonte.fileUrl) {
    return `
      <div class="${classe}"${estilo}>
        <video src="${escapeHtml(fonte.fileUrl)}" ${capa ? `poster="${escapeImg(capa)}"` : ''} controls preload="none" playsinline></video>
      </div>`;
  }

  // Sem JavaScript na página publicada: o quadro começa com uma capa própria
  // (`srcdoc`) e só chama o serviço de vídeo depois do clique no play. Assim a
  // página não conversa com o YouTube/Vimeo antes de a pessoa querer assistir.
  const interno = `<style>
    html,body{margin:0;height:100%;background:#0b1220;}
    a{display:block;position:relative;height:100%;text-decoration:none;}
    img{width:100%;height:100%;object-fit:cover;display:block;}
    .s{position:absolute;inset:0;background:linear-gradient(180deg,rgba(6,8,14,.15),rgba(6,8,14,.55));}
    .p{position:absolute;top:50%;left:50%;width:78px;height:78px;margin:-39px 0 0 -39px;border-radius:999px;background:rgba(255,255,255,.18);box-shadow:0 18px 50px -18px rgba(0,0,0,.8),inset 0 0 0 1px rgba(255,255,255,.5);}
    .p::after{content:'';position:absolute;top:26px;left:31px;border-style:solid;border-width:13px 0 13px 22px;border-color:transparent transparent transparent #fff;}
    .l{position:absolute;bottom:16px;left:0;right:0;text-align:center;color:rgba(255,255,255,.86);font:600 13px/1.2 system-ui,sans-serif;letter-spacing:.08em;text-transform:uppercase;}
  </style>
  <a href="${escapeHtml(fonte.embedUrl)}" target="_self" rel="noopener">
    ${capa ? `<img src="${escapeImg(capa)}" alt="">` : ''}
    <span class="s"></span><span class="p"></span>
    ${rotulo ? `<span class="l">${escapeHtml(rotulo)}</span>` : ''}
  </a>`;

  return `
    <div class="${classe}"${estilo}>
      <iframe srcdoc="${escapeHtml(interno)}" title="Vídeo" loading="lazy" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>
    </div>`;
};

/**
 * Forma decorativa (Fase 6). É só enfeite: quando o meio não suporta
 * (impressão, e-mail), nada é impresso.
 */
const compileUiShape: BlockHtmlCompiler = (block, data) => {
  const p = block.props ?? {};
  const modo = (data?.__renderMode as StudioVisualRenderMode) ?? 'published';
  const classe = classeDaForma(propTexto(p.shape), propTexto(p.size), modo);
  if (!classe) return '';
  const cor = propTexto(p.color);
  return `<div class="${escapeHtml(classe)}${movimentoHtml(p)}"${
    cor ? ` style="--st-shape:${escapeHtml(cor)}"` : ''
  } aria-hidden="true"></div>`;
};

/**
 * Seção em HTML livre: a montagem é feita pelo módulo único
 * `htmlSections.ts`, o mesmo usado pelo editor. Em e-mail nada é impresso.
 */
const compileSectionHtml: BlockHtmlCompiler = (block, data) => {
  const modo = (data?.__renderMode as string) ?? 'published';
  return renderStudioHtmlSection(
    (block.props ?? {}) as StudioHtmlSectionProps,
    modo === 'pdf' || modo === 'print' ? 'pdf' : modo === 'email' ? 'email' : 'published',
  );
};

/**
 * Registro de compiladores por chave de bloco.
 * Blocos ausentes aqui (por exemplo `proposal.status` e todos os `action.*`)
 * simplesmente não produzem saída impressa — é comportamento esperado.
 */
const HTML_BLOCK_COMPILERS: Record<string, BlockHtmlCompiler> = {
  'proposal.hero': compileProposalHero,
  'proposal.items': compileProposalItems,
  'proposal.totals': compileProposalTotals,
  'proposal.conditions': compileProposalConditions,
  'proposal.footer': compileProposalFooter,
  'common.text': compileCommonText,
  'common.image': compileCommonImage,
  'common.button': compileCommonButton,
  'common.divider': compileCommonDivider,
  'layout.spacer': compileLayoutSpacer,
  'layout.divider': compileCommonDivider,
  'layout.section': compileLayoutSection,
  'layout.columns': compileLayoutColumns,
  'layout.stack': compileLayoutStack,
  'page.hero': compilePageHero,
  'page.features': compilePageFeatures,
  'page.testimonials': compilePageTestimonials,
  'page.form': compilePageForm,
  'page.heading': compilePageHeading,
  'page.split': compilePageSplit,
  'page.gallery': compilePageGallery,
  'page.video': compilePageVideo,
  'page.stats': compilePageStats,
  'page.logos': compilePageLogos,
  'page.pricing': compilePagePricing,
  'page.faq': compilePageFaq,
  'page.steps': compilePageSteps,
  'page.team': compilePageTeam,
  'page.cta': compilePageCta,
  'page.deadline': compilePageDeadline,
  'page.contact': compilePageContact,
  'page.nav': compilePageNav,
  'page.footer': compilePageFooter,
  'page.banner': compilePageBanner,
  // Primitivos e seção com camadas (motor de modelos)
  'section.layered': compileLayeredSection,
  'ui.badge': compileUiBadge,
  'ui.title': compileUiTitle,
  'ui.paragraph': compileUiParagraph,
  'ui.buttons': compileUiButtons,
  'ui.image': compileUiImage,
  'ui.card': compileUiCard,
  'ui.avatars': compileUiAvatars,
  'ui.stat': compileUiStat,
  'ui.logos': compileUiLogos,
  'ui.device': compileUiDevice,
  'ui.video': compileUiVideo,
  'ui.shape': compileUiShape,
  // Seção em HTML livre (página criada por IA e importada como blocos)
  'section.html': compileSectionHtml,
  // Blocos interativos oficiais: o desenho vem do módulo único `interactive.ts`,
  // o mesmo usado pelo editor, pela página publicada, pelo PDF e pelo e-mail.
  ...Object.fromEntries(
    listStudioInteractiveBlocks().map((chave) => [
      chave,
      ((block, data) =>
        renderStudioInteractiveBlock(
          chave,
          (block?.props ?? {}) as Record<string, unknown>,
          ((data?.__renderMode as string) === 'print'
            ? 'pdf'
            : ((data?.__renderMode as string) ?? 'published')) as StudioInteractiveMode,
        )) as BlockHtmlCompiler,
    ]),
  ),
};

/** Compila um bloco isolado (usado também pelos blocos com filhos). */
function compileBlockHtml(block: StudioHtmlBlock, data: StudioHtmlData): string {
  const compiler = HTML_BLOCK_COMPILERS[block?.type];
  if (!compiler) return '';
  const modoDaTela = (data?.__renderMode as StudioEffectRenderMode) ?? 'published';
  // Direção de arte (Fase 7): bloco marcado como "só na tela" nem chega ao PDF
  // e ao e-mail — e vice-versa.
  if (!blocoVisivelNoModo(block?.props, modoDaTela)) return '';
  try {
    let html = compiler(block, data) ?? '';
    if (!html) return '';
    // Efeito visual (Fase 4): envolve o bloco apenas quando há efeito aplicável
    // ao modo de saída atual (o substituto já vem resolvido pelo registro).
    if (lerEfeito(block?.props)) {
      const modo = (data?.__renderMode as StudioEffectRenderMode) ?? 'published';
      const classe = classeDoEfeito(block?.props, modo);
      if (classe) html = `<div class="${escapeHtml(classe)}">${html}</div>`;
    }
    // Fundo decorativo e acabamento de borda (biblioteca própria, Fase 1):
    // entram só quando o bloco pediu e o meio de saída suporta.
    if (temDecor(block?.props)) {
      const modoDecor = (data?.__renderMode as StudioDecorRenderMode) ?? 'published';
      const classeDecor = classesDoDecor(block?.props, modoDecor);
      if (classeDecor) {
        const estiloDecor = estiloDoDecor(block?.props);
        html = `<div class="${escapeHtml(classeDecor)}"${
          estiloDecor ? ` style="${escapeHtml(estiloDecor)}"` : ''
        }>${html}</div>`;
      }
    }
    // Direção de arte (Fase 7): visibilidade por tela e ordem dentro da faixa.
    if (temRegraDeTela(block?.props)) {
      const classeTela = classeDeVisibilidade(block?.props, modoDaTela);
      const ordem = estiloDaOrdem(block?.props);
      if (classeTela || ordem) {
        html = `<div${classeTela ? ` class="${escapeHtml(classeTela)}"` : ''}${ordem ? ` style="${escapeHtml(ordem)}"` : ''}>${html}</div>`;
      }
    }
    // Camada (Fase 3): envolve apenas quando o bloco pediu posicionamento.
    if (!temCamada(block?.props)) return html;
    const estilo = estiloDaCamada(block?.props);
    return `<div class="${escapeHtml(classesDaCamada(block?.props))}"${estilo ? ` style="${estilo}"` : ''}>${html}</div>`;
  } catch (_) {
    // Um bloco com defeito nunca pode derrubar o documento inteiro.
    return '';
  }
}

/** Compila os filhos de um bloco contêiner. */
function compileChildren(block: StudioHtmlBlock, data: StudioHtmlData): string {
  const filhos = Array.isArray(block.children) ? block.children : [];
  return filhos.map((filho) => compileBlockHtml(filho, data)).filter(Boolean).join('\n');
}


/** Chaves de bloco suportadas pela saída HTML (útil para validação/editor) */
export function listHtmlSupportedBlocks(): string[] {
  return Object.keys(HTML_BLOCK_COMPILERS);
}

// =============================================================================
// Compilação do documento
// =============================================================================

/** Compila apenas o corpo (sem `<html>`), útil para e-mail e pré-visualização */
export function renderStudioBody(doc: StudioHtmlDocument, data: StudioHtmlData): string {
  const blocks = Array.isArray(doc?.blocks) ? doc.blocks : [];
  // Cada documento começa com o orçamento de interação zerado.
  ORCAMENTO_MOVIMENTO = criarOrcamentoDeMovimento(
    (data?.__renderMode as StudioMotionRenderMode) ?? 'published',
  );
  return blocks
    .map((block) => compileBlockHtml(block, data))
    .filter(Boolean)
    .join('\n');
}

/** Compila o documento completo em HTML pronto para conversão em PDF */
export function renderStudioHtml(
  doc: StudioHtmlDocument,
  data: StudioHtmlData,
  options?: { title?: string; extraCss?: string },
): StudioHtmlResult {
  const body = renderStudioBody(doc, data);
  const html = `
  <!DOCTYPE html>
  <html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(options?.title ?? 'Documento')}</title>
    <style>${baseStyles()}${options?.extraCss ?? ''}</style>
  </head>
  <body class="studio-doc">
${body}
  </body>
  </html>`;
  return { html, compilerVersion: STUDIO_COMPILER_VERSION };
}

/**
 * Documento padrão da proposta impressa.
 * Espelha `src/lib/studio/templates/defaultProposalTemplate.ts`, sem os blocos
 * de ação (que só existem na saída web).
 */
export const DEFAULT_PROPOSAL_PRINT_DOCUMENT: StudioHtmlDocument = {
  schemaVersion: 1,
  theme: {},
  settings: {},
  blocks: [
    { id: 'hero', type: 'proposal.hero', props: {} },
    { id: 'items', type: 'proposal.items', props: {} },
    { id: 'totals', type: 'proposal.totals', props: {} },
    { id: 'conditions', type: 'proposal.conditions', props: {} },
    { id: 'footer', type: 'proposal.footer', props: {} },
  ],
};
